import React, { useState, useEffect } from 'react';
import {
  Download,
  Upload,
  Database,
  Settings,
  FileText,
  Users,
  Clock,
  DollarSign,
  Calendar,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  ExternalLink,
  Key,
  Shield,
  Zap
} from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface ExportHistory {
  id: string;
  fileName: string;
  format: string;
  dataTypes: string[];
  fileSize: number;
  recordCount: number;
  createdAt: string;
  status: string;
}

interface IntegrationSettings {
  accounting: any;
  crm: any;
  email: any;
  storage: any;
  webhooks: any;
}

export const DataExportIntegration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'export' | 'integrations'>('export');
  const [exportHistory, setExportHistory] = useState<ExportHistory[]>([]);
  const [integrationSettings, setIntegrationSettings] = useState<IntegrationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    format: 'excel',
    dataTypes: ['clients'],
    dateRange: {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    },
    includeDeleted: false,
    includeMetadata: true
  });

  const tabs = [
    {
      id: 'export' as const,
      label: 'Data Export',
      icon: Download,
      description: 'Export your data in various formats'
    },
    {
      id: 'integrations' as const,
      label: 'Integrations',
      icon: Zap,
      description: 'Manage external system integrations'
    }
  ];

  const dataTypes = [
    { id: 'clients', label: 'Clients', icon: Users, description: 'Client information and contacts' },
    { id: 'users', label: 'Users', icon: Users, description: 'Staff and user accounts' },
    { id: 'tasks', label: 'Tasks', icon: FileText, description: 'Tasks and project data' },
    { id: 'invoices', label: 'Invoices', icon: DollarSign, description: 'Billing and invoice data' },
    { id: 'timesheets', label: 'Timesheets', icon: Clock, description: 'Time tracking records' },
    { id: 'attendance', label: 'Attendance', icon: Calendar, description: 'Attendance records' },
    { id: 'all', label: 'All Data', icon: Database, description: 'Complete data export' }
  ];

  const exportFormats = [
    { id: 'excel', label: 'Excel (.xlsx)', description: 'Spreadsheet format with multiple sheets' },
    { id: 'csv', label: 'CSV', description: 'Comma-separated values' },
    { id: 'json', label: 'JSON', description: 'JavaScript Object Notation' },
    { id: 'xml', label: 'XML', description: 'Extensible Markup Language' },
    { id: 'pdf', label: 'PDF', description: 'Portable Document Format (summary)' },
    { id: 'zip', label: 'ZIP Archive', description: 'Multiple formats in compressed archive' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [historyResponse, settingsResponse] = await Promise.all([
        api.get('/firm-admin/data/exports'),
        api.get('/firm-admin/integrations')
      ]);

      if (historyResponse.data.success) {
        setExportHistory(historyResponse.data.data.exports);
      }

      if (settingsResponse.data.success) {
        setIntegrationSettings(settingsResponse.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.post('/firm-admin/data/export', exportOptions);
      
      if (response.data.success) {
        toast.success('Export completed successfully');
        fetchData(); // Refresh export history
        
        // Auto-download the file
        const downloadUrl = `/firm-admin/data/export/${response.data.data.fileName}`;
        window.open(downloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Error exporting data:', error);
      toast.error(error.response?.data?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = async (fileName: string) => {
    try {
      const downloadUrl = `/firm-admin/data/export/${fileName}`;
      window.open(downloadUrl, '_blank');
      toast.success('Download started');
    } catch (error: any) {
      console.error('Error downloading file:', error);
      toast.error('Download failed');
    }
  };

  const handleDeleteExport = async (fileName: string) => {
    if (!confirm('Are you sure you want to delete this export file?')) {
      return;
    }

    try {
      await api.delete(`/firm-admin/data/export/${fileName}`);
      toast.success('Export file deleted');
      fetchData();
    } catch (error: any) {
      console.error('Error deleting export:', error);
      toast.error('Failed to delete export file');
    }
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2 text-lg">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Data Export & Integrations</h3>
          <p className="text-sm text-gray-500">Export your data and manage external integrations</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className={`mr-2 h-5 w-5 ${
                  activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                }`} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'export' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Export Configuration */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Export Configuration</h4>
              
              {/* Data Types */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Data Types</label>
                <div className="space-y-2">
                  {dataTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <label key={type.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={exportOptions.dataTypes.includes(type.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setExportOptions(prev => ({
                                ...prev,
                                dataTypes: [...prev.dataTypes, type.id]
                              }));
                            } else {
                              setExportOptions(prev => ({
                                ...prev,
                                dataTypes: prev.dataTypes.filter(t => t !== type.id)
                              }));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <Icon className="w-4 h-4 ml-2 mr-2 text-gray-400" />
                        <div>
                          <span className="text-sm font-medium text-gray-900">{type.label}</span>
                          <p className="text-xs text-gray-500">{type.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Format Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Export Format</label>
                <select
                  value={exportOptions.format}
                  onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {exportFormats.map((format) => (
                    <option key={format.id} value={format.id}>
                      {format.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {exportFormats.find(f => f.id === exportOptions.format)?.description}
                </p>
              </div>

              {/* Date Range */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Date Range</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={exportOptions.dateRange.startDate}
                      onChange={(e) => setExportOptions(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, startDate: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Date</label>
                    <input
                      type="date"
                      value={exportOptions.dateRange.endDate}
                      onChange={(e) => setExportOptions(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, endDate: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Options</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeDeleted}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeDeleted: e.target.checked }))}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Include deleted records</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeMetadata}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeMetadata: e.target.checked }))}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Include metadata</span>
                  </label>
                </div>
              </div>

              {/* Export Button */}
              <button
                onClick={handleExport}
                disabled={exporting || exportOptions.dataTypes.length === 0}
                className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Export History */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Export History</h4>
              
              {exportHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Download className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-gray-900 mb-2">No Exports Yet</h3>
                  <p className="text-sm text-gray-500">Your export history will appear here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {exportHistory.map((exportItem) => (
                    <div key={exportItem.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <FileText className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="font-medium text-gray-900">{exportItem.fileName}</span>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            exportItem.status === 'completed' ? 'text-green-800 bg-green-100' : 'text-yellow-800 bg-yellow-100'
                          }`}>
                            {exportItem.status === 'completed' ? (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            ) : (
                              <AlertCircle className="w-3 h-3 mr-1" />
                            )}
                            {exportItem.status}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          <span>{exportItem.dataTypes.join(', ')} • </span>
                          <span>{formatFileSize(exportItem.fileSize)} • </span>
                          <span>{exportItem.recordCount} records • </span>
                          <span>{formatDate(exportItem.createdAt)}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDownload(exportItem.fileName)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteExport(exportItem.fileName)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Integrations Tab
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cloud Storage Integration */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <Database className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Cloud Storage</h4>
                    <p className="text-sm text-gray-500">Sync exports to cloud storage</p>
                  </div>
                </div>
                <button className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Configure
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-green-600">GD</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">Google Drive</div>
                      <div className="text-xs text-gray-500">Auto-sync enabled</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-xs text-green-600">Connected</span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-blue-600">DB</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">Dropbox</div>
                      <div className="text-xs text-gray-500">Not connected</div>
                    </div>
                  </div>
                  <button className="text-xs text-blue-600 hover:text-blue-700">Connect</button>
                </div>

                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-purple-600">OD</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">OneDrive</div>
                      <div className="text-xs text-gray-500">Not connected</div>
                    </div>
                  </div>
                  <button className="text-xs text-blue-600 hover:text-blue-700">Connect</button>
                </div>
              </div>
            </div>

            {/* Accounting Software Integration */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Accounting Software</h4>
                    <p className="text-sm text-gray-500">Sync financial data</p>
                  </div>
                </div>
                <button className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Configure
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-blue-600">QB</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">QuickBooks</div>
                      <div className="text-xs text-gray-500">Sync invoices & payments</div>
                    </div>
                  </div>
                  <button className="text-xs text-blue-600 hover:text-blue-700">Connect</button>
                </div>

                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-orange-600">XR</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">Xero</div>
                      <div className="text-xs text-gray-500">Sync financial data</div>
                    </div>
                  </div>
                  <button className="text-xs text-blue-600 hover:text-blue-700">Connect</button>
                </div>

                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-red-600">FW</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">FreshBooks</div>
                      <div className="text-xs text-gray-500">Sync time & expenses</div>
                    </div>
                  </div>
                  <button className="text-xs text-blue-600 hover:text-blue-700">Connect</button>
                </div>
              </div>
            </div>

            {/* CRM Integration */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">CRM Systems</h4>
                    <p className="text-sm text-gray-500">Sync client data</p>
                  </div>
                </div>
                <button className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Configure
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-blue-600">SF</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">Salesforce</div>
                      <div className="text-xs text-gray-500">Sync contacts & opportunities</div>
                    </div>
                  </div>
                  <button className="text-xs text-blue-600 hover:text-blue-700">Connect</button>
                </div>

                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-orange-600">HS</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">HubSpot</div>
                      <div className="text-xs text-gray-500">Sync marketing & sales data</div>
                    </div>
                  </div>
                  <button className="text-xs text-blue-600 hover:text-blue-700">Connect</button>
                </div>

                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
                      <span className="text-xs font-bold text-green-600">PP</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">Pipedrive</div>
                      <div className="text-xs text-gray-500">Sync pipeline data</div>
                    </div>
                  </div>
                  <button className="text-xs text-blue-600 hover:text-blue-700">Connect</button>
                </div>
              </div>
            </div>

            {/* Webhook Integration */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
                    <Zap className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Webhooks</h4>
                    <p className="text-sm text-gray-500">Real-time data sync</p>
                  </div>
                </div>
                <button className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Manage
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div>
                    <div className="text-sm font-medium">Report Generated</div>
                    <div className="text-xs text-gray-500">https://api.example.com/webhook</div>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-xs text-green-600">Active</span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div>
                    <div className="text-sm font-medium">Export Completed</div>
                    <div className="text-xs text-gray-500">https://api.example.com/export</div>
                  </div>
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-xs text-gray-500">Inactive</span>
                  </div>
                </div>

                <button className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-gray-400 hover:text-gray-600">
                  + Add New Webhook
                </button>
              </div>
            </div>
          </div>

          {/* API Keys Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-medium text-gray-900">API Keys & Authentication</h4>
                <p className="text-sm text-gray-500">Manage API keys for external integrations</p>
              </div>
              <button className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                <Key className="w-4 h-4 mr-2" />
                Generate API Key
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <div className="text-sm font-medium">Production API Key</div>
                  <div className="text-xs text-gray-500 font-mono">erpca_prod_••••••••••••••••••••••••••••••••</div>
                  <div className="text-xs text-gray-400 mt-1">Created: 2024-01-15 • Last used: 2 hours ago</div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-600 hover:bg-gray-100 rounded" title="Copy">
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-red-600 hover:bg-red-100 rounded" title="Revoke">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <div className="text-sm font-medium">Development API Key</div>
                  <div className="text-xs text-gray-500 font-mono">erpca_dev_••••••••••••••••••••••••••••••••</div>
                  <div className="text-xs text-gray-400 mt-1">Created: 2024-01-10 • Last used: Never</div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-600 hover:bg-gray-100 rounded" title="Copy">
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-red-600 hover:bg-red-100 rounded" title="Revoke">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Status */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Integration Status</h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">1</div>
                <div className="text-sm text-gray-500">Active Integrations</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">247</div>
                <div className="text-sm text-gray-500">Data Syncs Today</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">99.9%</div>
                <div className="text-sm text-gray-500">Uptime</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
